import React, { useState } from "react";
import { Grid, TextField, MenuItem, IconButton, Popover, Slider, Typography, Box } from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import { VoiceBotOptions } from "../types";

export default function VadSettings({
    options,
    setOptions,
    isDisabled,
}: {
    options: Partial<VoiceBotOptions>;
    setOptions: (options: Partial<VoiceBotOptions>) => void;
    isDisabled: boolean;
}) {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (event: any) => {
        if (!isDisabled) {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? "vad-settings-popover" : undefined;

    const handleSliderChange = (key: string) => (_: any, value: number | number[]) => {
        if (typeof value === "number") {
            setOptions({ [key]: value });
        }
    };

    return (
        <Grid item sx={{ display: "flex", alignItems: "center" }}>
            <TextField
                select
                size="small"
                label="VAD"
                title="Voice Activity Detection"
                disabled={isDisabled}
                value={options.vadThreshold || ""}
                onChange={({ target }) => {
                    setOptions({
                        vadThreshold: parseFloat(target.value) ?? undefined,
                    });
                }}
                sx={{ minWidth: "100px" }}
            >
                {["", 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9].map((voiceSpeed) => (
                    <MenuItem key={`v${voiceSpeed}`} value={`${voiceSpeed}`}>
                        {voiceSpeed || "Default"}
                    </MenuItem>
                ))}
            </TextField>

            <IconButton aria-describedby={id} onClick={handleClick} size="small" sx={{ ml: 1 }} disabled={isDisabled}>
                <SettingsIcon />
            </IconButton>

            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
            >
                <Box sx={{ p: 2, width: 250 }}>
                    {[
                        {
                            label: `Min Non-Speech (${options.vadMinNonSpeech}ms)`,
                            key: "vadMinNonSpeech",
                            min: 0,
                            max: 10000,
                            value: options.vadMinNonSpeech,
                        },
                        {
                            label: `Min Utterance (${options.vadMinUtterance}ms)`,
                            key: "vadMinUtterance",
                            min: 0,
                            max: 3000,
                            value: options.vadMinUtterance,
                        },
                        {
                            label: `Max Utterance (${options.vadMaxUtterance}ms)`,
                            key: "vadMaxUtterance",
                            min: 1000,
                            max: 60000,
                            value: options.vadMaxUtterance,
                        },
                    ].map(({ label, key, min, max, value }) => (
                        <Box key={key} sx={{ mb: 2 }}>
                            <Typography gutterBottom>{label}</Typography>
                            <Slider
                                value={value || 0}
                                onChange={handleSliderChange(key)}
                                step={100}
                                min={min}
                                max={max}
                                valueLabelDisplay="auto"
                            />
                        </Box>
                    ))}
                </Box>
            </Popover>
        </Grid>
    );
}
